import classNames from 'classnames';
import { Icon } from 'palette/Icon';
import { Image } from 'palette/Image';
import { BodyText, TextHighlighter } from 'palette/Typography';
import { useActiveDeal } from '@common/ActivateDeals/hooks';
import { CartItemPriceV2 } from '@common/Checkout/shared/components/CartItem/CartItemPriceV2';
import { useConfig } from '@common/Hooks/useConfig';
import type { ProductCard } from '@common/SearchResultsPage/types';
import { BEMBlock } from '@helpers/classnames';
import { isFeatureActive } from '@helpers/index';
import { SWISS_COUNTRY_CODE } from '../constants/countryCodes';
import { addSizeToImageUrl, extractProductInfo } from '../helpers';
import { extractCurrencyFromPrice, formatOptinPrice, formatPriceAndCurrency } from '../helpers/price';
import type { GlobalSearchProduct } from '../types';
import GlobalSearchRow from './GlobalSearchRow';
import '../styles/GlobalSearchProductCard.scss';

interface GlobalSearchProductCardProps {
    index: number;
    isMediaDiscountVoucher: boolean;
    product: GlobalSearchProduct | ProductCard;
    searchTerm: string;
    onSearchProductClick: (eanId: number, index: number, productId: number, variantId: number) => void;
}

function GlobalSearchProductCard({
    index,
    isMediaDiscountVoucher,
    product,
    searchTerm,
    onSearchProductClick,
}: GlobalSearchProductCardProps) {
    const config = useConfig();
    const isPriceStrategies = isFeatureActive('PLT-90698_podium_suggestions_unify_products_response_with_srp', config);
    const {
        actualPrice,
        basePrice,
        brandTitle,
        eanId,
        imageUrl,
        isDiscounted,
        mdvPrices,
        optinDiscountValue,
        prices,
        productId,
        title,
        url,
        variantId,
    } = extractProductInfo(product, isPriceStrategies);
    const deal = useActiveDeal();
    const { store } = config;
    const [block, BEMElement] = BEMBlock('global-search-product-card');
    const currency = extractCurrencyFromPrice(basePrice);
    const price = formatOptinPrice(optinDiscountValue, store);
    const discountedPrice = isDiscounted ? actualPrice : null;
    const isSwissStore = store === SWISS_COUNTRY_CODE;
    const formattedPriceWithCurrency = formatPriceAndCurrency(isSwissStore, price, currency);
    const optinPrice = optinDiscountValue ? formattedPriceWithCurrency : null;
    const finalPrice = isMediaDiscountVoucher ? optinPrice : discountedPrice;
    const isActiveDealsData = deal?.activated && mdvPrices?.length > 0;

    const handleProductClick = () => onSearchProductClick(eanId, index, productId, variantId);

    return (
        <div
            className={classNames(block)}
            data-product-id={productId}
        >
            <GlobalSearchRow
                href={url}
                onClick={handleProductClick}
            >
                <div className={BEMElement('image-container')}>
                    <Image
                        className={BEMElement('image')}
                        height="108"
                        src={addSizeToImageUrl(imageUrl, 'w183')}
                        width="83"
                    />
                </div>
                <div className={BEMElement('product-info-container')}>
                    <div className={BEMElement('product-info')}>
                        <div className={BEMElement('header')}>
                            <TextHighlighter
                                color="strong"
                                isRegex={false}
                                value={searchTerm}
                                weight="bold"
                            >
                                <BodyText level="1">{brandTitle}</BodyText>
                            </TextHighlighter>
                            <BodyText
                                level="2"
                                overflowType="ellipsis"
                            >
                                {title}
                            </BodyText>
                        </div>
                        {isPriceStrategies ? (
                            <CartItemPriceV2
                                productPrices={isActiveDealsData ? mdvPrices : prices}
                            />
                        ) : (
                            <>
                                {finalPrice && (
                                    <BodyText
                                        color="attention"
                                        level="2"
                                        weight="bold"
                                    >
                                        {finalPrice}
                                    </BodyText>
                                )}
                                <BodyText
                                    className={BEMElement('price', { previous: !!finalPrice })}
                                    level="2"
                                    weight="bold"
                                >
                                    {basePrice}
                                </BodyText>
                            </>
                        )}
                    </div>
                    <Icon
                        className={BEMElement('icon')}
                        color="grey-800"
                        name="palette_chevron_right"
                        size="small"
                    />
                </div>
            </GlobalSearchRow>
        </div>
    );
}

export default GlobalSearchProductCard;
